import { Analytics } from '@cobliteam/analytics';
import type { LocalUserPreferences } from '@cobliteam/api-lib';
import { useAiRecommendationsStore } from '@cobliteam/hooks';
import { permissionsConfig } from '@cobliteam/hooks/src/useAiRecommendationsStore/useAiRecommendationsStore.ts';
import { useTranslation } from '@cobliteam/i18n/src/hooks/useTranslation.ts';
import usePermissions from '@cobliteam/permissions/src/hooks/usePermissions.tsx';
import { Button, Popover, Tooltip } from '@cobliteam/ui/components';
import { PopoverContent, PopoverTrigger } from '@cobliteam/ui/components/Popover';
import Typography from '@cobliteam/ui/components/Typography';
import { useLocalStorage } from '@cobliteam/ui/hooks';
import Icon from '@cobliteam/ui/icons';
import { useCallback, useState } from 'react';
import AiRecommendationTasks from './AiRecommendationTasks.tsx';
import AiRecommendationTaskPopoverSkeleton from './components/AiRecommendationTaskPopoverSkeleton.tsx';

const AiRecommendationTasksPopover = () => {
  const { t } = useTranslation('common');
  const { isAccessAllowed } = usePermissions();

  const [isOpen, setIsOpen] = useState(false);
  const [preferences, setPreferences] = useLocalStorage<LocalUserPreferences>('localUserPreferences');

  const { recommendations, improvementRecommendations, progress, isLoading, isError, refetch } = useAiRecommendationsStore();
  // progress still used for the totalJourneys guard below

  const allowedRecommendations = recommendations.filter((item) => {
    return isAccessAllowed(permissionsConfig[item.journeyId]);
  });

  const allowedImprovementRecommendations = improvementRecommendations.filter((item) => {
    return isAccessAllowed(permissionsConfig[item.journeyId]);
  });

  const hideOnboardingBadge = !preferences?.hideOnboardingBadge;

  const handleOpen = useCallback(() => {
    setIsOpen((prev) => {
      if (!prev) {
        setPreferences((prevPrefs) => ({
          ...prevPrefs,
          hideOnboardingBadge: true,
        }));
      }
      return !prev;
    });

    Analytics.track('Activation Tasks - Sidebar button clicked');
  }, [setPreferences]);

  if (allowedRecommendations.length === 0 && allowedImprovementRecommendations.length === 0) {
    return null;
  }

  if (progress.totalJourneys === 0 && !isLoading) {
    return null;
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpen}>
      <Tooltip content='Ver recomendações inteligentes'>
        <PopoverTrigger asChild>
          <div className='relative mt-1.5'>
            <Button
              data-testid='app-header-activation-tasks-button'
              className='text-default hover:text-purple-12'
              variant='surface'
              color='purple'
              size='sm'
              icon={<Icon name='sparkles' color='purple' />}
              aria-label='Ver recomendações inteligentes'
            />
            {hideOnboardingBadge && (
              <span className='-top-0.5 -right-0.5 absolute flex size-2'>
                <span className='absolute inline-flex h-full w-full animate-ping rounded-full bg-accessory-purple-11 opacity-75' />
                <span className='relative inline-flex size-2 rounded-full bg-accessory-purple-11' />
              </span>
            )}
          </div>
        </PopoverTrigger>
      </Tooltip>
      <PopoverContent align='end' side='right' showArrow={false} className='!p-0 flex w-[450px] flex-col overflow-hidden'>
        {/* Header */}
        <div className='flex items-start justify-between gap-3 border-neutral-100 border-b px-4 py-3'>
          <div className='flex flex-col gap-0.5'>
            <Typography variant='heading-md'>Recomendações para a sua frota</Typography>
            <Typography variant='description-list-default'>Separamos algumas recomendações para você ganhar mais eficiência na sua operação</Typography>
          </div>
        </div>

        {isLoading ? (
          <AiRecommendationTaskPopoverSkeleton />
        ) : isError ? (
          <div className='flex flex-col items-center gap-4 px-4 py-6'>
            <div className='flex h-10 w-10 items-center justify-center rounded-full border-2 border-red-500'>
              <Icon name='circle-alert' size='sm' className='text-red-500' />
            </div>
            <div className='flex flex-col items-center gap-0.5 text-center'>
              <Typography variant='description-title'>Algo deu errado</Typography>
              <Typography variant='description-list-default' color='secondary'>
                Não conseguimos gerar suas recomendações no momento.
              </Typography>
            </div>
            <Button type='button' variant='outlined' color='default' size='condensed' onClick={refetch}>
              Tentar novamente
            </Button>
          </div>
        ) : (
          <AiRecommendationTasks
            recommendations={allowedRecommendations}
            improvementRecommendations={allowedImprovementRecommendations}
            allCompleted={progress.totalJourneys > 0 && progress.completedJourneys >= progress.totalJourneys}
          />
        )}
      </PopoverContent>
    </Popover>
  );
};

export default AiRecommendationTasksPopover;
